import { createTheme } from '@mui/material/styles';
import colors from './components/simple/Colors';

/**
 * @typedef {import('@mui/material/styles').Theme} Theme
 * @typedef {import('@mui/material/styles').ThemeOptions} ThemeOptions
 */

/** @type {Theme} */
const theme = createTheme({
    palette: {
        primary: {
            main: "#962EFF",
            contrastText: "#ffffff",
        },
        secondary: {
            main: "#962EFF",
            contrastText: "#ffffff",
        },
        text: {
            primary: colors.brand[25],
            dark_primary: colors.text[900],
            secondary: colors.gray[700],
            disabled: colors.gray[500],
            gray: colors.base.gray,
            tertiary: colors.base.tertiary,
            placeholder: colors.gray[500],
            placeholder_subtle: colors.gray[300],
            brand_primary: colors.brand[900],
            brand_secondary: colors.brand[200],
            button_hover: colors.brand[300],
            error_primary: colors.error[700],
            warning_primary: colors.warning[700],
            success_primary: colors.success[700],
        },
        border: {
            primary: colors.gray[300],
            secondary: colors.gray[200],
            tertiary: colors.brand[600],
            disabled: colors.gray[300],
            brand: colors.brand[500],
            error: colors.error[200],
            warning: colors.warning[200],
            success: colors.success[200],
        },
        foreground: {
            primary: colors.gray[900],
            secondary: colors.gray[700],
            tertiary: colors.gray[600],
            disabled: colors.gray[400],
            brand_primary: colors.brand[600],
            button_primary: colors.brand[900],
            button_secondary: colors.brand[100],
            badge_icon: colors.brand[200],
            error_primary: colors.error[500],
            warning_primary: colors.warning[500],
            success_primary: colors.success[500],
        },
        background: {
            primary: colors.brand[800],
            secondary: colors.gray[50],
            tertiary: colors.gray[100],
            quaternary: colors.gray[200],
            disabled: colors.gray[100],
            overlay: colors.gray[950],
            brand_primary: colors.base.white,
            brand_alt: colors.brand[700],
            button_primary: colors.brand[300],
            button_primary_hover: colors.brand[400],
            error_primary: colors.error[50],
            warning_primary: colors.warning[50],
            success_primary: colors.success[50],
        },
    },
    typography: {
        fontFamily: 'Poppins, Helvetica',
        fontWeightLight: 400,
        fontWeightRegular: 500,
        fontWeightMedium: 600,
        fontWeightBold: 700,
        displayXL: { fontSize: '3.75rem', lineHeight: '4.5rem' },
        displayLG: { fontSize: '3rem', lineHeight: '3.75rem' },
        displayMD: { fontSize: '2.375rem', lineHeight: '2.75rem' },
        displaySM: { fontSize: '1.875rem', lineHeight: '2.375rem' },
        displayXS: { fontSize: '1.5rem', lineHeight: '2rem' },
        textXL: { fontSize: '1.25rem', lineHeight: '1.875rem' },
        textLG: { fontSize: '1.125rem', lineHeight: '1.75rem' },
        textMD: { fontSize: '1rem', lineHeight: '1.5rem' },
        textSM: { fontSize: '0.875rem', lineHeight: '1.25rem' },
        textXS: { fontSize: '0.75rem', lineHeight: '1.125rem' },
    },
    shape: {
        borderRadius: 6,
        radius: {
            none: "0rem",
            xxs: "0.125rem",
            xs: "0.25rem",
            sm: "0.375rem",
            md: "0.5rem",
            lg: "0.625rem",
            xl: "0.75rem",
            "2xl": "1rem",
            "3xl": "1.25rem",
            "4xl": "1.5rem",
            full: "9999px",
        },
    },
    spacing: (factor) => `${0.25 * factor}rem`,
    customSpacing: {
        none: "0rem",
        xxs: "0.125rem",
        xs: "0.25rem",
        sm: "0.375rem",
        md: "0.5rem",
        lg: "0.75rem",
        xl: "1rem",
        "2xl": "1.25rem",
        "3xl": "1.5rem",
        "4xl": "2rem",
        "5xl": "2.5rem",
        "6xl": "3rem",
        "7xl": "4rem",
        "8xl": "5rem",
        "9xl": "6rem",
        "10xl": "8rem",
        "11xl": "10rem",
    },
    widths: {
        xxs: "20rem",
        xs: "24rem",
        sm: "30rem",
        md: "35rem",
        lg: "40rem",
        xl: "48rem",
        "2xl": "64rem",
        "3xl": "80rem",
        "4xl": "90rem",
        "5xl": "100rem",
        "6xl": "120rem",
    },
    containers: {
        paddingMobile: "1rem",
        paddingDesktop: "2rem",
        maxWidthDesktop: "80rem",
    },
    paragraph: {
        maxWidth: "45rem",
    },
});

export default theme;
